
import { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/auth/useAuth";
import { useAuthRedirect } from "@/hooks/auth/useAuthRedirect";
import { supabase } from "@/integrations/supabase/client";

export const useRegister = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const { redirectAfterSignUp } = useAuthRedirect();
  const { toast } = useToast();
  
  // Load saved email if any
  useEffect(() => {
    const savedEmail = localStorage.getItem("signup_email");
    if (savedEmail) {
      setEmail(savedEmail);
      localStorage.removeItem("signup_email");
    }
  }, []);
  
  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      console.log("Starting registration with:", { email, username });
      
      const result = await signUp({ email, password, username });
      console.log("Registration result:", result);
      
      if (result?.user?.id) {
        console.log("User created successfully, ID:", result.user.id);
        setUserId(result.user.id);

        // Send welcome email (fire-and-forget — don't block signup on email delivery)
        supabase.functions.invoke("send-welcome-email", {
          body: { userId: result.user.id, email, displayName: username },
        }).then(({ error }) => {
          if (error) console.error("Welcome email failed (non-blocking):", error);
        });

        // Show onboarding immediately after successful registration
        setShowOnboarding(true);
      } else if (result?.error?.message === "Display name already taken") {
        setError("This username is already taken. Please choose another one.");
        toast({
          title: "Username taken",
          description: "This username is already in use. Please choose another one.",
          variant: "destructive",
        });
      } else {
        console.error("User creation failed or returned unexpected result");
        // Account may have been created but email confirmation is required, or an unexpected error occurred.
        // Stay on the register page rather than redirecting to login.
        setError("Your account was created but requires email confirmation. Please check your inbox and verify your email, then sign in.");
        toast({
          title: "Check your email",
          description: "We've sent a confirmation link. Verify your email then sign in.",
        });
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      setError(error.message || "An unexpected error occurred");
      
      toast({
        title: "Registration failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };
  
  const onOnboardingComplete = () => {
    // After onboarding is complete, go to account page
    toast({
      title: "Profile setup complete",
      description: "Welcome to IntelliStitch! Your account is now ready to use.",
    });
    redirectAfterSignUp();
  };
  
  return {
    email,
    setEmail,
    password,
    setPassword,
    username,
    setUsername,
    loading,
    showOnboarding,
    setShowOnboarding,
    userId,
    error,
    handleRegister,
    onOnboardingComplete
  };
};
